import { Fragment, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, BookOpen, FileText, MessageSquareText, PenLine } from 'lucide-react';
import { PhotoLightbox, type LightboxPhoto } from '@repo/photo-lightbox';
import { cn } from '@repo/ui/src/lib/utils';
import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import { Button } from '@repo/ui/src/components/ui/button';
import {
  fullStop, prevMonthKey, progressDisplayName, sentence, sessionRatingMeta,
  type HomeworkPhotoRead, type MonthlyReviewStatus, type ProgressTimelineItem, type ProgressTimelineMonth, type StudentWithClient,
} from '@repo/shared-types';
import { EmptyState } from '../../../components/EmptyState';
import { useSubjects } from '../../../hooks/useSubjects';
import { ResponsiveSheet } from '../../../components/ResponsiveSheet';
import { fmtHouseDate } from '../../../lib/appliedDate';
import { formatMonthKey, weekdayLabel } from '../../../lib/sessionTime';
import { useProgressTimeline, useStudentMonthNotes } from '../../../hooks/useProgress';
import { ReviewEditorPanel } from '../../reviews/components/ReviewEditorPanel';
import { ReviewStatusCard } from './ReviewStatusCard';
import { splitPhotoStrip } from './photoStripLayout';

/** A subject/month with zero sessions still resolves successfully (the API
 *  pre-seeds every requested month key with zeroed aggregates) — but a
 *  defensive fallback for the one case that DOESN'T (student not found —
 *  `months: []`) keeps the editor openable instead of stuck on a skeleton. */
const ZERO_AGGREGATES: ProgressTimelineMonth['aggregates'] = {
  sessions: 0, present: 0, excused: 0, absent: 0, notes: 0,
};

/** The route's own clamp (`apps/api/src/routes/students.ts`, `months` 1..24).
 *  The client must know it: without it the load-more button renders at 24,
 *  asks for 27, gets 24 back, and only then disappears — one click that
 *  changed nothing. */
const TIMELINE_MONTH_CAP = 24;

/** "Th 5" + "27/08" off the session's UTC-naive wall clock. `fmtHouseDate`
 *  (already the house rule for the date part) plus `weekdayLabel`
 *  (`lib/sessionTime.ts`, the progress feature's own "Th N"/"CN" format —
 *  never a browser-local formatter, which pushes every evening session to
 *  the next day, INVARIANTS §2, shipped twice already). */
function dayParts(iso: string): { dow: string; date: string } {
  return { dow: weekdayLabel(iso), date: fmtHouseDate(iso, 'short') };
}

const HOMEWORK_STRIP_MAX = 4;

/** D3 (E4 frame 5, handoff 21 §3): the read-only 36px thumbnail strip under
 *  the BTVN lines, indented to hang under the BTVN TEXT rather than under
 *  its `book-open` icon (`ml-[19.4px]` = the icon's 13px width + the row's
 *  own 6.4px gap, matching the mock's `margin-left: 19.4px` literal). Own
 *  `PhotoLightbox` instance per session row, same pattern as the parent
 *  page's sibling `PhotoStrip` (`apps/web/app/components/progress/
 *  SessionCard.tsx`) — mounting several is safe, only one can have a
 *  non-null index open at a time.
 *
 *  The lightbox's per-photo "Bài chung"/"Riêng {tên}" caption fragment needs
 *  the count of leading CLASS photos to know where the split falls, but
 *  `homeworkPhotos` here is one flat, already-ordered array (server-side,
 *  §3.10) with no boundary marker — the split exists in
 *  `apps/api/src/services/progressTimeline.ts` (`classPhotos.length`) but
 *  never reaches this payload, and task 8 says not to add it. So every
 *  photo gets an empty `captionScope` and the lightbox's bottom bar shows
 *  the bare `lightbox.homework` label instead of guessing a scope. */
function HomeworkPhotoRow({ photos, subtitle }: { photos: HomeworkPhotoRead[]; subtitle: string }) {
  const { t } = useTranslation('progress');
  const [index, setIndex] = useState<number | null>(null);
  const { shown, overflow } = splitPhotoStrip(photos, HOMEWORK_STRIP_MAX);
  const lightboxPhotos: LightboxPhoto[] = photos.map((p) => ({
    id: p.id, url: p.url, width: p.width, height: p.height, captionScope: '',
  }));

  return (
    <div className="mt-[6.4px] ml-[19.4px] flex items-center gap-[4px]">
      {shown.map((p, i) => (
        <button
          key={p.id}
          type="button"
          aria-label={t('lightbox.homework')}
          onClick={() => setIndex(i)}
          className="h-[36px] w-[36px] shrink-0 overflow-hidden rounded-[6px] border border-surface-200 bg-surface-100 p-0"
        >
          <img src={p.url} alt="" loading="lazy" className="h-full w-full object-cover" />
        </button>
      ))}
      {overflow > 0 && (
        <button
          type="button"
          onClick={() => setIndex(shown.length)}
          className="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-[6px] bg-surface-900 text-[12px] font-semibold text-white"
        >
          {t('notes.photos.more', { n: overflow })}
        </button>
      )}
      <PhotoLightbox
        photos={lightboxPhotos}
        index={index}
        onIndexChange={setIndex}
        subtitle={() => subtitle}
        labels={{
          homework: t('lightbox.homework'),
          download: t('lightbox.download'),
          close: t('lightbox.close'),
          remove: t('notes.photos.remove'),
          counter: (i, n) => t('lightbox.counter', { i, n }),
        }}
      />
    </div>
  );
}

export interface ReviewTarget {
  month: string;
  subjectId: string;
  subjectName: string;
  /** Present only when reopening an EXISTING review (the "Sửa" link) — lets
   *  the editor prefill instead of starting blank. */
  existingContent?: string;
  /** THI-348: carried with `existingContent` so the editor knows it opened a draft. */
  existingStatus?: MonthlyReviewStatus;
  existingAiDrafted?: boolean;
}

function MonthBlock({ m, onWriteReview }: { m: ProgressTimelineMonth; onWriteReview: (target: ReviewTarget) => void }) {
  const { t } = useTranslation('progress');
  // The timeline payload carries no templates; the (cached) subject list
  // does, and only the "written before the template existed" line needs it.
  const subjects = useSubjects();
  const subjectsById = new Map((subjects.data ?? []).map((s) => [s.id, s]));

  const reviewedSubjectIds = new Set(
    m.items.filter((i): i is Extract<ProgressTimelineItem, { kind: 'REVIEW' }> => i.kind === 'REVIEW')
      .map((i) => i.subjectId),
  );
  // Subjects the student had a NOTED session in this month, first-seen order
  // — the only subject list this payload carries (items only include noted
  // sessions; the API already dropped the rest). One "Viết nhận xét" link per
  // subject that doesn't have a review yet — a student can be enrolled in
  // more than one subject in the same month, and an existing Piano review
  // must not hide the missing Toán one.
  const subjectsThisMonth = new Map<string, string>();
  for (const item of m.items) {
    if (item.kind === 'SESSION' && !subjectsThisMonth.has(item.subjectId)) {
      subjectsThisMonth.set(item.subjectId, item.subjectName);
    }
  }
  const missingReviewSubjects = [...subjectsThisMonth].filter(([id]) => !reviewedSubjectIds.has(id));
  const sessionItems = m.items.filter(
    (i): i is Extract<ProgressTimelineItem, { kind: 'SESSION' }> => i.kind === 'SESSION',
  );

  return (
    <section className="mb-8">
      {/* Month header — never a card, sits on the bare page background. */}
      <div className="mb-2.5 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <h2 className="font-display text-[15px] font-semibold tracking-[-0.012em] text-surface-800">
          {t('progress.monthTitle', { month: formatMonthKey(m.month) })}
        </h2>
        <p className="text-[12.5px] text-surface-500">{t('progress.monthMeta', { ...m.aggregates })}</p>
        {missingReviewSubjects.map(([subjectId, subjectName], i) => (
          <button
            key={subjectId}
            type="button"
            onClick={() =>
              onWriteReview({ month: m.month, subjectId, subjectName })
            }
            className={cn(
              'inline-flex min-h-[44px] items-center gap-1 text-[12.5px] font-medium text-brand-600 sm:min-h-0',
              i === 0 && 'ml-auto',
            )}
          >
            <PenLine className="h-3.5 w-3.5" strokeWidth={2} />
            {/* Subject name only when it's needed to tell two links apart —
                the single-subject case (the common one) matches the mock's
                plain "Viết nhận xét tháng 7" exactly. */}
            {missingReviewSubjects.length > 1 && <span className="text-surface-400">{subjectName} · </span>}
            {t('progress.writeReview', { month: Number(m.month.split('-')[1]) })}
          </button>
        ))}
      </div>

      {/* The review, when this month has one — the ONE tinted block in the
          tab. Reviews render first, then the session card below (the API
          already orders `m.items` that way; do not re-sort). */}
      {m.items.map((item: ProgressTimelineItem) =>
        item.kind === 'REVIEW' ? (
          <div
            key={`r-${item.subjectId}`}
            className="mb-4 rounded-lg border border-brand-200 bg-brand-50 px-5 py-4"
          >
            <div className="mb-2 flex flex-wrap items-center gap-2.5">
              <FileText className="h-[14px] w-[14px] shrink-0 text-brand-700" strokeWidth={2} />
              <span className="text-[12.5px] font-semibold text-brand-700">
                {t('progress.reviewHeading', { subject: item.subjectName })}
              </span>
              <span className="text-[12px] text-surface-500">
                {/* aiDrafted is internal and stays internal: it never leaves
                    this tab for a parent-facing surface. */}
                {t('progress.reviewMeta', {
                  teacher: item.teacherName ?? '—',
                  date: fmtHouseDate(item.updatedAt, 'short'),
                })}
                {item.aiDrafted && item.status !== 'DRAFT' ? ` · ${t('review.aiDrafted')}` : ''}
              </span>
              {item.status === 'DRAFT' && (
                <span className="rounded-full bg-attention-tint px-2 py-0.5 text-[11px] font-semibold text-attention-foreground">
                  {t('review.draftTag')}
                </span>
              )}
              <button
                type="button"
                onClick={() =>
                  onWriteReview({
                    month: item.month,
                    subjectId: item.subjectId,
                    subjectName: item.subjectName,
                    existingContent: item.content,
                    existingStatus: item.status,
                    existingAiDrafted: item.aiDrafted,
                  })
                }
                className="ml-auto min-h-[44px] text-[12.5px] font-medium text-brand-600 sm:min-h-0"
              >
                {t('progress.editReview')}
              </button>
            </div>
            <p className="whitespace-pre-line text-sm leading-[1.55] text-surface-800">{item.content}</p>
          </div>
        ) : null,
      )}

      {/* Session rows live inside a white card: outer horizontal padding
          only, each row carries its own vertical padding + hairline. */}
      {sessionItems.length > 0 && (
        <div className="rounded-lg border border-surface-200 bg-surface-0 px-5">
          {sessionItems.map((item) => {
            const { dow, date } = dayParts(item.startAt);
            const subjectHasTemplate = (id: string) => Boolean(subjectsById.get(id)?.noteTemplate);
            // THI-193: sectioned homework, one line per item ("Tên mục: nội
            // dung", name bold) then the free text, the way the parent page
            // lists them. One " · "-joined line read as a wall of text once a
            // real teacher wrote four items (5/9/2026).
            const homeworkLines: { name: string | null; text: string }[] = [
              ...item.classHomeworkItems.map((i) => ({ name: i.name, text: i.content })),
              ...item.myHomeworkItems.map((i) => ({ name: i.name, text: i.content })),
              ...[item.classHomework, item.myHomework].filter((v): v is string => Boolean(v)).map((text) => ({ name: null, text })),
            ];
            const hasMyItems = item.myItems.length > 0;
            const hasMyText = hasMyItems || Boolean(item.myComment);
            // Written as free text before the subject got a template: say so
            // (handoff 19 §6) rather than looking like a note that ignored it.
            const legacyFree = Boolean(item.myComment) && !hasMyItems && subjectHasTemplate(item.subjectId);
            // When there's no personal note, the class note (if any) IS the
            // primary line — the whole row reads muted, chip and text
            // inline, matching the mock's 18/08 row exactly. A personal
            // note present pushes the class note down into its own smaller,
            // indented, always-muted line instead (the 27/08 row).
            return (
              <div
                key={item.sessionId}
                className="grid grid-cols-[64px_1fr] gap-4 border-b border-surface-100 py-4 last:border-b-0"
              >
                <div className="text-[12.5px] leading-[1.4] tabular-nums text-surface-500">
                  {dow} <strong className="font-semibold text-surface-800">{date}</strong>
                  <br />
                  <span className="text-[11.5px]">{item.subjectName}</span>
                </div>
                <div
                  className={cn(
                    'text-sm leading-[1.5]',
                    hasMyText ? 'text-surface-800' : 'text-surface-500',
                  )}
                >
                  {/* THI-346: the level leads the personal block, emoji first,
                      the way the parent page shows it. Null when unrated or
                      when the subject's switch is off (the API gates it). */}
                  {item.myRating && (
                    <div className="mb-1.5 flex items-center gap-1.5 text-[13px] font-medium text-surface-800">
                      <span className="text-[20px] leading-none" aria-hidden="true">{sessionRatingMeta(item.myRating).emoji}</span>
                      {sessionRatingMeta(item.myRating).label}
                    </div>
                  )}
                  {hasMyItems ? (
                    <div className="grid grid-cols-[104px_1fr] gap-x-3 gap-y-1.5">
                      {item.myItems.map((i) => (
                        <Fragment key={i.itemId}>
                          <span className="pt-0.5 text-[12.5px] font-medium text-surface-500">{i.name}</span>
                          <span className="whitespace-pre-line">{fullStop(i.content)}</span>
                        </Fragment>
                      ))}
                      {item.myComment && (
                        <>
                          <span className="pt-0.5 text-[12.5px] font-medium text-surface-500">{t('notes.extra.label')}</span>
                          <span className="whitespace-pre-line">{fullStop(item.myComment)}</span>
                        </>
                      )}
                    </div>
                  ) : (
                    item.myComment
                  )}
                  {legacyFree && <div className="mt-1 text-[12px] text-surface-400">{t('progress.legacyFree')}</div>}
                  {(item.classComment || item.classItems.length > 0) && (
                    hasMyText ? (
                      <div className="mt-1.5 text-[13px] text-surface-500">
                        <span className="mr-2 inline-block rounded-full bg-surface-100 px-2 text-[11px] font-semibold text-surface-600">
                          {t('progress.classChip')}
                        </span>
                        {classLine(item)}
                      </div>
                    ) : (
                      <>
                        <span className="mr-2 inline-block rounded-full bg-surface-100 px-2 text-[11px] font-semibold text-surface-600">
                          {t('progress.classChip')}
                        </span>
                        {classLine(item)}
                      </>
                    )
                  )}
                  {homeworkLines.length > 0 && (
                    <div className="mt-1.5 flex items-start gap-2 text-[13px] text-surface-500">
                      <BookOpen className="mt-[3px] h-[13px] w-[13px] shrink-0" strokeWidth={1.8} />
                      <div className="min-w-0">
                        <span className="font-medium text-surface-600">{t('progress.homeworkLabel')}</span>
                        {homeworkLines.map((h, i) => (
                          <div key={i} className="whitespace-pre-line">
                            {h.name && <span className="font-medium">{h.name}: </span>}
                            {h.name ? sentence(h.text) : h.text}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {item.homeworkPhotos.length > 0 && (
                    <HomeworkPhotoRow
                      photos={item.homeworkPhotos}
                      subtitle={`${item.subjectName} · ${dow} ${date}`}
                    />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

/**
 * The Tiến độ học tập tab body: a month-by-month notes timeline on the left,
 * a 300px aside slot on the right (desktop only — Task 13 fills it with the
 * Sổ liên lạc block; this component just reserves the space and renders
 * whatever it's given, even nothing yet).
 */
/** The shape of one loaded month (header line, review box, two session rows)
 *  while the first timeline page is in flight. Without it the tab flashed
 *  the "Chưa có ghi chú nào" empty state at every student on every cold
 *  load (INVARIANTS §4: pending is neither empty nor error). */
function TimelineSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true">
      <div className="flex items-baseline gap-3">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-3.5 w-56" />
      </div>
      <Skeleton className="h-24 w-full rounded-lg" />
      <div className="space-y-3 rounded-lg border border-surface-200 bg-surface-0 p-4">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

/** Class-note line: sectioned items as "<b>Tên mục:</b> nội dung" joined
 *  with " · ", then the free text (handoff 19 §6, D3 keeps class notes one line). */
function classLine(item: Extract<ProgressTimelineItem, { kind: 'SESSION' }>) {
  const parts: ReactNode[] = item.classItems.map((i, idx) => (
    <Fragment key={i.itemId}>
      {idx > 0 && ' · '}
      <span className="font-medium">{i.name}:</span> {sentence(i.content)}
    </Fragment>
  ));
  if (item.classComment) parts.push(<Fragment key="free">{parts.length > 0 && ' · '}{item.classComment}</Fragment>);
  return <>{parts}</>;
}

export function ProgressTab({ student, aside }: { student: StudentWithClient; aside: ReactNode }) {
  const { t } = useTranslation('progress');
  const { t: tCommon } = useTranslation('common');
  const [months, setMonths] = useState(3);
  const [reviewTarget, setReviewTarget] = useState<ReviewTarget | null>(null);
  // Mirrors ReviewEditorPanel's own `dirty` computation outward so
  // ResponsiveSheet's backdrop-tap/Escape/swipe-down guard can ask the same
  // question the panel's in-editor "Bỏ qua" guard already asks internally.
  const [editorDirty, setEditorDirty] = useState(false);
  const { data, isPending: timelinePending } = useProgressTimeline(student.id, months);
  const displayName = progressDisplayName(student.name, student.nickname);

  // Subject-scoped counts for the editor Sheet. The month's own `aggregates`
  // above are summed across EVERY subject the student takes that month —
  // fine for the timeline header, wrong for the editor, which shows them as
  // belonging to the ONE subject being reviewed ("{{subject}} · {{sessions}}
  // buổi…") and gates the AI button on `row.noteCount`. A two-subject
  // student opening the subject WITHOUT notes must not see the other
  // subject's counts, nor an AI button that reads as ready when it isn't.
  // Same query `ReviewEditorPanel` calls internally for its notes list — the
  // identical queryKey dedupes to one request, not two.
  const subjectStats = useStudentMonthNotes(
    reviewTarget ? student.id : null,
    reviewTarget?.subjectId ?? '',
    reviewTarget?.month ?? '',
  );
  // `months[0]` is missing only when the student lookup itself failed (API
  // returns `months: []`) — everything else pre-seeds a zeroed bucket for
  // the exact month asked for. Falling back to ZERO_AGGREGATES rather than
  // leaving this undefined means a resolved-but-empty response still opens
  // the editor (teacher writes from memory; AI stays locked at 0 notes)
  // instead of sitting on the skeleton forever.
  const subjectAggregates = subjectStats.isSuccess
    ? (subjectStats.data.months[0]?.aggregates ?? ZERO_AGGREGATES)
    : undefined;
  // THI-348: the status card's "Viết" opens a month that may already hold a
  // DRAFT (a draft is not "written", so the card still offers Viết). Load it
  // rather than opening blank over it.
  const savedReview = subjectStats.data?.months[0]?.items.find(
    (i): i is Extract<ProgressTimelineItem, { kind: 'REVIEW' }> => i.kind === 'REVIEW',
  );

  const nonEmpty = (data?.months ?? []).filter((m) => m.items.length > 0);
  // The window is capped to the student's activity span (Task 4) — there is
  // no `hasMore` field, so "the cap didn't bite" (more history may exist) is
  // simply the API having returned exactly as many months as asked for.
  const requestDidNotCap = (data?.months.length ?? 0) === months;
  const atCap = months >= TIMELINE_MONTH_CAP;
  const oldestShown = data?.months.at(-1)?.month;
  // The label promises "tháng {M} và trước đó" — M is the month the click WILL
  // reveal (one before what is already on screen), not the oldest month
  // already visible; `prevMonthKey` is exactly this string-math step
  // (shared-types), never `new Date(month)` + setMonth (a bare 'YYYY-MM'
  // parses inconsistently across engines).
  const nextLoadMonth = oldestShown ? prevMonthKey(oldestShown) : undefined;
  const loadMoreButton = nextLoadMonth && requestDidNotCap && !atCap && (
    <button
      type="button"
      onClick={() => setMonths((n) => Math.min(n + 3, TIMELINE_MONTH_CAP))}
      className="min-h-[44px] text-[12.5px] font-medium text-brand-600 hover:underline"
    >
      {t('progress.loadMore', { month: formatMonthKey(nextLoadMonth) })}
    </button>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
      {/* Phone puts the aside FIRST (the Sổ liên lạc block is the thing a
          teacher comes here to copy); desktop keeps it at the right. The
          "Nhận xét tháng" status card stacks below it on every width (three
          rows on phone, six on desktop; Johnny 2026-09-04 reversed the
          desktop-only ruling of port guide §4.6). */}
      <div className="flex flex-col gap-4 lg:order-2">
        {aside}
        <ReviewStatusCard reviewMonths={data?.reviewMonths ?? []} pending={timelinePending} onWrite={setReviewTarget} />
      </div>

      <div className="min-w-0 lg:order-1">
        {timelinePending ? (
          <TimelineSkeleton />
        ) : nonEmpty.length === 0 ? (
          <>
            <EmptyState
              kind="teach"
              icon={MessageSquareText}
              title={t('progress.emptyTitle', { name: displayName })}
              body={t('progress.emptyBody')}
              // No "Mở suất học hôm nay" CTA: this page doesn't already hold
              // a cheap answer to "does this student have a session today"
              // (no today-schedule data is fetched anywhere on the student
              // detail page), so per the task's own fallback the CTA is
              // dropped rather than adding a new query just for the empty
              // state.
            />
            {/* The fetched window can be all-empty (e.g. a paused student)
                while OLDER notes still exist beyond it — the empty state
                must not dead-end a teacher who knows there's history. */}
            {loadMoreButton}
          </>
        ) : (
          <>
            {nonEmpty.map((m) => <MonthBlock key={m.month} m={m} onWriteReview={setReviewTarget} />)}
            {loadMoreButton}
          </>
        )}
      </div>

      {/* The per-student path into the SAME editor the sweep uses (handoff §2:
          "tab Tiến độ của em mở cùng editor trong Sheet"). The roster row is
          built from `subjectStats` (subject-scoped), not from the month's own
          aggregates — see the comment above. `mode="single"` (Task 5) drops
          the position counter and the sweep's "Lưu và sang học viên tiếp";
          `onDirtyChange` reports the panel's own dirty state up to
          `editorDirty` so ResponsiveSheet's own backdrop-tap/Escape guard
          can ask the same question. */}
      <ResponsiveSheet
        open={reviewTarget !== null}
        onOpenChange={(o) => { if (!o) { setReviewTarget(null); setEditorDirty(false); } }}
        a11yTitle={t('progress.asideReviews')}
        title={t('review.title', { month: formatMonthKey(reviewTarget?.month ?? '') })}
        description={reviewTarget?.subjectName}
        dirty={editorDirty}
        discard={{
          title: t('notes.discardTitle'),
          body: t('notes.discardBody'),
          confirm: t('notes.discardConfirm'),
          cancel: t('notes.discardCancel'),
        }}
      >
        {reviewTarget && (
          subjectStats.isError ? (
            // The gate must not be "truthy data" alone — an error leaves
            // `data` (and so `subjectAggregates`) permanently undefined,
            // which would otherwise sit on the skeleton forever with no
            // way out. ResponsiveSheet's own X / outside-click / Escape
            // already covers "close"; this only needs to offer a way forward.
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <AlertTriangle className="h-8 w-8 text-destructive" strokeWidth={1.8} />
              <p className="max-w-sm text-sm text-surface-500">{t('review.loadError')}</p>
              <Button variant="outline" size="sm" onClick={() => subjectStats.refetch()}>
                {tCommon('routeError.retry')}
              </Button>
            </div>
          ) : subjectAggregates ? (
            <ReviewEditorPanel
              key={`${reviewTarget.subjectId}-${reviewTarget.month}`}
              row={{
                studentId: student.id,
                studentName: student.name,
                nickname: student.nickname ?? null,
                hasReview: (reviewTarget.existingStatus ?? savedReview?.status) === 'PUBLISHED',
                reviewStatus: reviewTarget.existingStatus ?? savedReview?.status ?? null,
                aiDrafted: false,
                reviewUpdatedAt: null,
                reviewTeacherName: null,
                noteCount: subjectAggregates.notes,
                sessions: subjectAggregates.sessions,
                present: subjectAggregates.present,
                excused: subjectAggregates.excused,
                absent: subjectAggregates.absent,
              }}
              subjectId={reviewTarget.subjectId}
              subjectName={reviewTarget.subjectName}
              month={reviewTarget.month}
              existingContent={reviewTarget.existingContent ?? savedReview?.content ?? ''}
              existingStatus={reviewTarget.existingStatus ?? savedReview?.status ?? null}
              existingAiDrafted={reviewTarget.existingAiDrafted ?? savedReview?.aiDrafted ?? false}
              position={1}
              total={1}
              mode="single"
              onDirtyChange={setEditorDirty}
              onSaved={() => { setReviewTarget(null); setEditorDirty(false); }}
              onSaveAndNext={() => { setReviewTarget(null); setEditorDirty(false); }}
            />
          ) : (
            // Still pending (never gated on "not error and no data yet" —
            // `subjectStats.isPending` is the real signal, `isError` above
            // already peeled off the other terminal state). Mounting the
            // panel before it resolves would flash `noteCount: 0` (AI
            // button reading "no notes") for a student who has plenty.
            <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-48 w-full" />
            </div>
          )
        )}
      </ResponsiveSheet>
    </div>
  );
}
